import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { GroupTemplateFormData } from './group-template-form-data';
import { ChangeEvent } from 'react';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

interface StepThreeProps {
  control: Control<GroupTemplateFormData>;
  setValue: UseFormSetValue<GroupTemplateFormData>;
  watch: UseFormWatch<GroupTemplateFormData>;
  getValues: UseFormGetValues<GroupTemplateFormData>;
}

const whoCanViewGroupOptions = [
  { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_VIEW' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_VIEW' },
];

const whoCanPostMessageOptions = [
  { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_POST' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_POST' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_POST' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_POST' },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_POST' },
  { displayValue: 'None can post', value: 'NONE_CAN_POST' },
];

const whoCanViewMembersOptions = [
  { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_VIEW' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
];
// whoCanViewMembership > whoCanModerateMembers
const whoCanModerateMembersOptions = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  // theres supposed to be an "entire organization" choice here
  // but its not in any of googles api or documentation
  { displayValue: 'None', value: 'NONE' },
];

const whoCanModerateContentOptions = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  { displayValue: 'None', value: 'NONE' },
];

const whoCanAssistContentOptions = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  { displayValue: 'None', value: 'NONE' },
];

const whoCanLeaveGroupOptions = [
  { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_LEAVE' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_LEAVE' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_LEAVE' },
  { displayValue: 'None', value: 'NONE_CAN_LEAVE' },
];

const inviteMembersOptions = [
  { displayValue: 'Anyone on the web can join', value: 'ANYONE_CAN_JOIN' },
  {
    displayValue: 'Organization users only',
    value: 'ALL_IN_DOMAIN_CAN_JOIN',
  },
  {
    displayValue: 'Organization users can ask',
    value: 'CAN_REQUEST_TO_JOIN',
  },
  { displayValue: 'Invited users only', value: 'INVITED_CAN_JOIN' },
];

const whoCanContactOwnerOptions = [
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_CONTACT' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_CONTACT' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_CONTACT' },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_CONTACT' },
];

// whoCanViewMembership > whoCanDiscoverGroup
const whoCanDiscoverGroupOptions = [
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_DISCOVER' },
  {
    displayValue: 'Entire organization',
    value: 'ALL_IN_DOMAIN_CAN_DISCOVER',
  },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_DISCOVER' },
];

export default function StepThree({
  control,
  setValue,
  watch,
  getValues,
}: StepThreeProps) {
  const { primaryDomain } = useUserSettings();

  const whoCanViewMembershipValue = watch('whoCanViewMembership');
  const getFilteredModerateOptions = (viewValue: string) => {
    switch (viewValue) {
      case 'ALL_IN_DOMAIN_CAN_VIEW':
      case 'ALL_MEMBERS_CAN_VIEW':
        return whoCanModerateMembersOptions;
      case 'ALL_MANAGERS_CAN_VIEW':
        return whoCanModerateMembersOptions.filter((option) =>
          ['OWNERS_ONLY', 'OWNERS_AND_MANAGERS', 'NONE'].includes(option.value)
        );
      case 'ALL_OWNERS_CAN_VIEW':
        return whoCanModerateMembersOptions.filter((option) =>
          ['OWNERS_ONLY', 'NONE'].includes(option.value)
        );
      default:
        return [];
    }
  };

  const getFilteredDiscoverOptions = (viewValue: string) => {
    switch (viewValue) {
      case 'ALL_IN_DOMAIN_CAN_VIEW':
        return whoCanDiscoverGroupOptions.filter((option) =>
          ['ALL_IN_DOMAIN_CAN_DISCOVER', 'ANYONE_CAN_DISCOVER'].includes(
            option.value
          )
        );
      default:
        return whoCanDiscoverGroupOptions;
    }
  };

  const whoCanViewMemberOnchange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newWhoCanViewMembership = e.target.value;
    // autoselect valid who can manage members
    const currentWhoCanModerateMembers = getValues('whoCanModerateMembers');

    const filteredOptions = getFilteredModerateOptions(newWhoCanViewMembership);

    const isCurrentValid = filteredOptions.some(
      (option) => option.value === currentWhoCanModerateMembers
    );

    if (!isCurrentValid && filteredOptions.length > 0) {
      const fullIndex = whoCanModerateMembersOptions.findIndex(
        (option) => option.value === currentWhoCanModerateMembers
      );
      const nextValid = filteredOptions.find(
        (option) =>
          whoCanModerateMembersOptions.findIndex(
            (o) => o.value === option.value
          ) > fullIndex
      );
      const newValue = (nextValid ?? filteredOptions[0]).value as
        | 'ALL_MEMBERS'
        | 'OWNERS_AND_MANAGERS'
        | 'OWNERS_ONLY'
        | 'NONE';
      setValue('whoCanModerateMembers', newValue);
    }

    // autoselect valid who can discover group
    const currentWhoCanDiscoverGroup = getValues('whoCanDiscoverGroup');

    const filteredDiscoverOptions = getFilteredDiscoverOptions(
      newWhoCanViewMembership
    );
    const isCurrentDiscoverValid = filteredDiscoverOptions.some(
      (option) => option.value === currentWhoCanDiscoverGroup
    );
    if (!isCurrentDiscoverValid && filteredDiscoverOptions.length > 0) {
      const fullDiscoverIndex = whoCanDiscoverGroupOptions.findIndex(
        (option) => option.value === currentWhoCanDiscoverGroup
      );
      const nextDisoverValid = filteredDiscoverOptions.find(
        (option) =>
          whoCanDiscoverGroupOptions.findIndex(
            (o) => o.value === option.value
          ) > fullDiscoverIndex
      );
      const newDiscoverValue = (nextDisoverValid ?? filteredDiscoverOptions[0])
        .value as
        | 'ANYONE_CAN_DISCOVER'
        | 'ALL_IN_DOMAIN_CAN_DISCOVER'
        | 'ALL_MEMBERS_CAN_DISCOVER';
      setValue('whoCanDiscoverGroup', newDiscoverValue);
    }
  };

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      <Stack alignItems="center" justifyContent={'space-evenly'}>
        <Grid sx={{ pl: 10, pt: 4 }} container spacing={4}>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanViewGroup"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanViewGroup',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanViewGroup',
                  }}
                  select
                  label="Who can view conversations"
                  helperText="Users can view conversations in Groups if web forum is enabled."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanViewGroupOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanPostMessage"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanPostMessage',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanPostMessage',
                  }}
                  select
                  label="Who can post"
                  helperText="Users can start and participate in group conversations."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanPostMessageOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanViewMembership"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanViewMembership',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanViewMembership',
                  }}
                  disabled={watch('whoCanViewGroup') === 'ANYONE_CAN_VIEW'}
                  select
                  label="Who can view members"
                  helperText="Users can see the members list for the group."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    whoCanViewMemberOnchange(e);
                  }}
                >
                  {whoCanViewMembersOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanModerateMembers"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanModerateMembers',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanModerateMembers',
                  }}
                  select
                  label="Who can manage members"
                  helperText="Control who is allowed to add and remove group members."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {getFilteredModerateOptions(whoCanViewMembershipValue).map(
                    (option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    )
                  )}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanModerateContent"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanModerateContent',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanModerateContent',
                  }}
                  select
                  label="Who can moderate content"
                  helperText="This includes approving, locking, and deleting messages and conversations."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanModerateContentOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanContactOwner"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanContactOwner',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanContactOwner',
                  }}
                  select
                  label="Who can contact group owners"
                  helperText="Control who can send messages to group owners"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanContactOwnerOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanAssistContent"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanAssistContent',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanAssistContent',
                  }}
                  select
                  label="Who can assist content"
                  helperText="Specifies who can moderate metadata"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanAssistContentOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanLeaveGroup"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanLeaveGroup',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanLeaveGroup',
                  }}
                  select
                  label="Who can leave group"
                  helperText="Specifies who can leave the group"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {whoCanLeaveGroupOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanDiscoverGroup"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanDiscoverGroup',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanDiscoverGroup',
                  }}
                  select
                  label="Who can discover group"
                  helperText="Specifies the set of users for whom this group is discoverable"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {getFilteredDiscoverOptions(whoCanViewMembershipValue).map(
                    (option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    )
                  )}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="whoCanJoin"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'whoCanJoin',
                  }}
                  InputLabelProps={{
                    htmlFor: 'whoCanJoin',
                  }}
                  select
                  label="Who can join group"
                  sx={{ width: 400 }}
                  value={field.value || 'Control who can join the group'}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value === 'ANYONE_CAN_JOIN') {
                      setValue('allowExternalMembers', true);
                    }
                  }}
                >
                  {inviteMembersOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="allowExternalMembers"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      disabled={watch('whoCanJoin') === 'ANYONE_CAN_JOIN'}
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  }
                  label={`Allow new users not in ${primaryDomain}`}
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
